import { useMutation, useQuery } from '@tanstack/react-query';

import { queryClient } from '~app/query-client';

import { baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';

import type {
  ConflictRulesDocument,
  UpdateConflictRulesDto,
} from '../model/recital';

export interface GetConflictRulesParams {
  recitalId: string;
}

export interface UpdateConflictRulesParams {
  recitalId: string;
  data: UpdateConflictRulesDto;
}

export const getConflictRulesAPI = async ({
  recitalId,
}: GetConflictRulesParams) => {
  const response = await baseAPI.get<ConflictRulesDocument>(
    `/recitals/${recitalId}/conflict-rules`,
  );
  return response.data;
};

export const updateConflictRulesAPI = async ({
  recitalId,
  data,
}: UpdateConflictRulesParams) => {
  const response = await baseAPI.post<ConflictRulesDocument>(
    `/recitals/${recitalId}/conflict-rules`,
    data,
  );
  return response.data;
};

export const useGetConflictRulesQuery = (recitalId: string) => {
  return useQuery({
    queryKey: [KEYS.CONFLICT_RULES, recitalId],
    queryFn: () => getConflictRulesAPI({ recitalId }),
    enabled: !!recitalId,
  });
};

export const useUpdateConflictRulesMutation = () => {
  return useMutation({
    mutationFn: async (params: UpdateConflictRulesParams) => {
      return await updateConflictRulesAPI(params);
    },
    onSuccess: (_, variables) => {
      // Invalidate conflict rules query to refresh data
      void queryClient.invalidateQueries({
        queryKey: [KEYS.CONFLICT_RULES, variables.recitalId],
      });
      // Also invalidate conflicts since rules affect conflict detection
      void queryClient.invalidateQueries({
        queryKey: [KEYS.CONFLICTS, variables.recitalId],
      });
    },
  });
};
